<template>
  <div class="choice-prompt" :class="{ archived: !isTappable }">
    <div v-if="interaction.prompt" class="choice-prompt-question">
      {{ interaction.prompt }}
    </div>
    <div class="pill-group" role="group">
      <button
        v-for="(option, index) in interaction.options"
        :key="index"
        type="button"
        class="pill-button"
        :class="pillClass(index)"
        :disabled="!isTappable"
        @click="select(index)"
      >
        {{ option }}
      </button>
    </div>
  </div>
</template>

<script>
import { useHaptics } from "@/composables/useHaptics.js";

export default {
  name: "ChoicePromptCard",
  props: {
    interaction: { type: Object, required: true },
    live: { type: Boolean, default: false },
  },
  emits: ["submit"],
  data() {
    return { localSelection: null };
  },
  computed: {
    // A stored response (reloaded chat) or a local tap both freeze the card.
    selectedIndex() {
      if (this.localSelection !== null) return this.localSelection;
      const stored = this.interaction.response;
      return stored && Number.isInteger(stored.selectedIndex)
        ? stored.selectedIndex
        : null;
    },
    isTappable() {
      return this.live && this.selectedIndex === null;
    },
  },
  watch: {
    // A failed submit re-arms the card (live flips back to true) but leaves the
    // optimistic pick set. Clear it so the learner can actually retry.
    live(isLive) {
      if (isLive && !this.interaction.response) {
        this.localSelection = null;
      }
    },
  },
  methods: {
    pillClass(index) {
      if (this.selectedIndex === null) return {};
      const chosen = this.selectedIndex === index;
      const { correctIndex } = this.interaction;
      if (Number.isInteger(correctIndex)) {
        return {
          chosen,
          correct: chosen && index === correctIndex,
          incorrect: chosen && index !== correctIndex,
          // reveal the right answer when the learner missed it
          revealed: !chosen && index === correctIndex && this.selectedIndex !== correctIndex,
        };
      }
      return { chosen };
    },
    select(index) {
      if (!this.isTappable) return;
      this.localSelection = index;
      const { correctIndex } = this.interaction;
      const wrong = Number.isInteger(correctIndex) && index !== correctIndex;
      useHaptics().impact(wrong ? "Medium" : "Light");
      this.$emit("submit", { type: "choicePrompt", selectedIndex: index });
    },
  },
};
</script>

<style scoped>
.choice-prompt {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--al-border);
}

.choice-prompt-question {
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.pill-group {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.pill-button {
  border: 1.5px solid var(--al-border);
  background: transparent;
  color: var(--al-text);
  border-radius: 999px;
  padding: 0.4rem 1rem;
  font-size: 0.95rem;
  cursor: pointer;
  transition: transform 0.12s ease, background-color 0.15s ease,
    border-color 0.15s ease;
}

.pill-button:not(:disabled):hover {
  border-color: var(--al-primary);
}

.pill-button:not(:disabled):active {
  transform: scale(0.96);
}

.pill-button:disabled {
  cursor: default;
  opacity: 0.7;
}

.pill-button.chosen {
  border-color: var(--al-primary);
  background-color: var(--al-primary-soft);
  opacity: 1;
}

.pill-button.correct {
  border-color: var(--al-success);
  background-color: color-mix(in srgb, var(--al-success) 15%, transparent);
}

.pill-button.incorrect {
  border-color: var(--al-danger);
  background-color: color-mix(in srgb, var(--al-danger) 12%, transparent);
  animation: shake 0.3s ease;
}

.pill-button.revealed {
  border-color: var(--al-success);
  border-style: dashed;
  opacity: 1;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-3px); }
  75% { transform: translateX(3px); }
}

@media (prefers-reduced-motion: reduce) {
  .pill-button.incorrect {
    animation: none;
  }
}
</style>
